import { useState } from 'react'
import { IconSmartphone } from '@holbonkit/icons'
import { Input, InputPhone } from '@holbonkit/ui'
import { clsx } from 'clsx'

import { getMaskedDisplayValue, getMaskSuffix, MASK_PRESETS, type TMaskPreset } from './masks'

import styles from './masked-input.module.scss'

type TMaskedInputProps = {
    'data-test-id'?: string
    errorText?: string
    label: string
    name: string
    onBlur: () => void
    onChange: (value: string) => void
    onFocus: () => void
    preset: TMaskPreset
    value: string
}

export const MaskedInput = ({
    'data-test-id': dataTestId,
    errorText,
    label,
    name,
    onBlur,
    onChange,
    preset,
    onFocus,
    value,
}: TMaskedInputProps) => {
    const [isFocused, setIsFocused] = useState(false)
    const { formatValue, mask } = MASK_PRESETS[preset]

    const displayValue = getMaskedDisplayValue(preset, value)
    const maskSuffix = getMaskSuffix(mask, displayValue)
    const isMaskVisible = isFocused && displayValue.length < mask.length

    const handleFocus = () => {
        setIsFocused(true)
        onFocus()
    }

    return (
        <div className={styles['masked-input']} data-test-id={dataTestId}>
            {isMaskVisible && (
                <span
                    aria-hidden
                    className={clsx(
                        styles['masked-input__overlay'],
                        preset === 'phoneNumber' && styles['masked-input__overlay--phone'],
                    )}
                >
                    <span className={styles['masked-input__overlay-value']}>{displayValue}</span>
                    <span className={styles['masked-input__overlay-mask']}>{maskSuffix}</span>
                </span>
            )}
            {preset === 'phoneNumber' ?
                <InputPhone
                    errorText={errorText}
                    inputMode='numeric'
                    label={label}
                    name={name}
                    size='lg'
                    startIcon={<IconSmartphone color='var(--text-and-icon-primary)' />}
                    value={value}
                    className={clsx(
                        styles['masked-input__control'],
                        isMaskVisible && styles['masked-input__control--masked'],
                    )}
                    onFocus={handleFocus}
                    onBlur={() => {
                        onBlur()
                        setIsFocused(false)
                    }}
                    onChange={(nextValue) => {
                        onChange(formatValue(nextValue))
                    }}
                />
            :   <Input
                    errorText={errorText}
                    inputMode='numeric'
                    label={label}
                    name={name}
                    size='lg'
                    value={value}
                    className={clsx(
                        styles['masked-input__control'],
                        isMaskVisible && styles['masked-input__control--masked'],
                    )}
                    onFocus={handleFocus}
                    onBlur={() => {
                        onBlur()
                        setIsFocused(false)
                    }}
                    onChange={(event) => {
                        onChange(formatValue(event.target.value))
                    }}
                />
            }
        </div>
    )
}
